import { AlertTriangle, CalendarClock, RefreshCw, Sparkles, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { useFinanceRunway } from '@/features/financials/hooks';

/**
 * Full-width AI banner that sits below the daily brief on the dashboard for
 * super-admins. It answers a single question — "when do we run out of money?"
 * (burning) or "how much cash do we finish the year with?" (profitable) — using
 * the `finance-runway-ai` Edge Function. The heavy lifting (reading the cash
 * position + monthly trend, prompting Claude) lives server-side; this component
 * only renders the verdict.
 */
export function RunwayBanner() {
  const { data, isLoading, isError, error, refetch, isFetching } = useFinanceRunway();
  const runway = data?.runway;

  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-muted-foreground" />
          <Skeleton className="h-4 w-40" />
        </div>
        <Skeleton className="mt-3 h-8 w-2/3" />
        <Skeleton className="mt-3 h-4 w-full max-w-3xl" />
      </div>
    );
  }

  if (isError || !runway) {
    return (
      <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-card p-5">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles className="h-4 w-4" />
          <span>
            Couldn't estimate your runway right now
            {error instanceof Error ? ` — ${error.message}` : ''}.
          </span>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          Retry
        </button>
      </div>
    );
  }

  const burning = runway.status === 'burning';
  const profitable = runway.status === 'profitable';

  // Palette follows the brand status accents: salmon for a cash burn (act
  // now), moss for a profitable trajectory, neutral linen when we can't tell.
  const tone = burning
    ? {
        wrap: 'border-fresh-salmon-dark/40 bg-fresh-salmon/10',
        icon: 'text-fresh-salmon-dark',
        Icon: AlertTriangle,
        eyebrow: 'Estimated day we run out of money',
      }
    : profitable
      ? {
          wrap: 'border-bright-moss-dark/40 bg-bright-moss/15',
          icon: 'text-bright-moss-dark',
          Icon: TrendingUp,
          eyebrow: 'Projected money at the end of the year',
        }
      : {
          wrap: 'border-border bg-card',
          icon: 'text-muted-foreground',
          Icon: CalendarClock,
          eyebrow: 'Cash runway',
        };
  const Icon = tone.Icon;

  // Headline number: the run-out date when burning, the year-end cash figure
  // when profitable. We prefer these structured fields (clamped to the real
  // cash position server-side) over parsing the model's prose.
  const headlineValue = burning
    ? runway.date
      ? formatDate(runway.date, 'long')
      : null
    : profitable && runway.projected_amount_cents != null
      ? formatCurrency(runway.projected_amount_cents, { whole: true })
      : null;

  return (
    <div className={cn('rounded-lg border p-5', tone.wrap)}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5" />
            {tone.eyebrow}
          </div>

          <div className="flex items-baseline gap-3">
            <Icon className={cn('h-6 w-6 shrink-0 translate-y-1', tone.icon)} />
            <span className="text-2xl font-medium tracking-headline text-foreground sm:text-3xl">
              {headlineValue ?? runway.headline}
            </span>
          </div>

          {/* When we have a hard number above, keep the model's one-liner as a
              supporting caption; otherwise the headline already carried it. */}
          {headlineValue && runway.headline && (
            <p className="text-sm text-foreground/80">{runway.headline}</p>
          )}

          {runway.reasoning && (
            <p className="max-w-3xl pt-1 text-sm text-muted-foreground">{runway.reasoning}</p>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-2 text-xs text-muted-foreground">
            <span>
              Avg monthly net:{' '}
              <span
                className={cn(
                  'font-medium tabular-nums',
                  runway.monthly_net_cents < 0
                    ? 'text-fresh-salmon-dark'
                    : runway.monthly_net_cents > 0
                      ? 'text-bright-moss-dark'
                      : 'text-foreground',
                )}
              >
                {runway.monthly_net_cents > 0 ? '+' : ''}
                {formatCurrency(runway.monthly_net_cents, { whole: true })}/mo
              </span>
            </span>
            {burning && runway.months_of_runway != null && (
              <span>
                Runway:{' '}
                <span className="font-medium tabular-nums text-foreground">
                  ~{runway.months_of_runway.toFixed(runway.months_of_runway < 10 ? 1 : 0)} months
                </span>
              </span>
            )}
            {runway.confidence && (
              <span className="capitalize">Confidence: {runway.confidence}</span>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          title="Re-run the estimate"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-card/60 px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          Refresh
        </button>
      </div>
    </div>
  );
}
